import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/redaction/")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { property: "og:description", content: "Connexion à l’espace privé de rédaction de Mathys Gautier." },
      { property: "og:title", content: "Connexion à la rédaction — Mathys Gautier" },
      { name: "description", content: "Connexion à l’espace privé de rédaction de Mathys Gautier." },{ title: "Connexion à la rédaction — Mathys Gautier" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: RedactionAuth,
});

function RedactionAuth() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Facteur TOTP à valider quand la 2FA est active sur le compte. */
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [failures, setFailures] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(0);

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      if (!data.user) return;
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.nextLevel === "aal2" && aal.currentLevel !== "aal2") return;
      navigate({ to: "/redaction/dashboard", replace: true });
    });
  }, [navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (honeypot) {
      await new Promise((r) => setTimeout(r, 1500));
      setError("Identifiants invalides");
      return;
    }
    if (Date.now() < lockedUntil) {
      setError("Trop de tentatives. Réessaie dans une minute.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        const n = failures + 1;
        setFailures(n);
        if (n >= 5) {
          setLockedUntil(Date.now() + 60_000);
          setFailures(0);
        }
        throw new Error("Identifiants invalides");
      }
      setFailures(0);

      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.nextLevel === "aal2" && aal.currentLevel !== "aal2") {
        const { data: factors, error: fErr } = await supabase.auth.mfa.listFactors();
        if (fErr) throw fErr;
        const totp = factors?.totp?.find((f) => f.status === "verified");
        if (!totp) throw new Error("Aucune application d’authentification enrôlée.");
        setMfaFactorId(totp.id);
        return;
      }

      navigate({ to: "/redaction/dashboard", replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaFactorId) return;
    setError(null);
    setLoading(true);
    try {
      const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({
        factorId: mfaFactorId,
      });
      if (cErr) throw cErr;
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challenge.id,
        code: code.trim(),
      });
      if (vErr) throw vErr;
      navigate({ to: "/redaction/dashboard", replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Code invalide");
    } finally {
      setLoading(false);
    }
  }

  async function handleCancelMfa() {
    await supabase.auth.signOut();
    setMfaFactorId(null);
    setCode("");
    setPassword("");
  }

  return (
    <section className="mx-auto flex min-h-[80vh] max-w-md flex-col justify-center px-6 py-16">
      <p className="eyebrow text-ocean">Espace privé</p>
      <h1 className="mt-4 font-serif text-4xl">Rédaction</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {mfaFactorId
          ? "Saisis le code à 6 chiffres de ton application d’authentification."
          : "Connexion à l'espace de rédaction."}
      </p>

      {mfaFactorId ? (
        <form onSubmit={handleVerify} className="mt-8 space-y-4">
          <div>
            <label
              className="block text-xs uppercase tracking-widest text-muted-foreground"
              htmlFor="otp"
            >
              Code de vérification
            </label>
            <input
              id="otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-paper px-3 py-2 text-sm tracking-[0.4em]"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={loading || code.trim().length < 6}
            className="w-full rounded-md bg-foreground px-4 py-2 text-sm text-background disabled:opacity-50"
          >
            {loading ? "…" : "Vérifier"}
          </button>
          <button
            type="button"
            onClick={() => void handleCancelMfa()}
            className="w-full rounded-md border border-border px-4 py-2 text-sm"
          >
            Annuler
          </button>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
            <label htmlFor="website">Site web</label>
            <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
          </div>
          <div>
            <label
              className="block text-xs uppercase tracking-widest text-muted-foreground"
              htmlFor="email"
            >
              E-mail
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-paper px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label
              className="block text-xs uppercase tracking-widest text-muted-foreground"
              htmlFor="password"
            >
              Mot de passe
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={6}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-paper px-3 py-2 text-sm"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-foreground px-4 py-2 text-sm text-background disabled:opacity-50"
          >
            {loading ? "…" : "Se connecter"}
          </button>
        </form>
      )}
    </section>
  );
}

